<template>
  <div class="create-agent">
    <el-dialog
      :model-value="visible"
      class="dialog-inner-scroller max-h-[600px]"
      :before-close="close"
      width="530"
      align-center
    >
      <template #header>
        <h4>
          {{ title }}
        </h4>
      </template>

      <el-form
        ref="formRef"
        class="add-platform-users-form w-[460px]"
        require-asterisk-position="right"
        :model="form"
        :rules="formRules"
      >
        <el-form-item prop="serverId" label="完税开票科目">
          {{ isNull(row?.invoiceSubName) ? '--' : row?.invoiceSubName }}
        </el-form-item>
        <el-form-item prop="amount" label="服务主体">
          {{ isNull(row?.serverName) ? '--' : row?.serverName }}
        </el-form-item>
        <el-form-item prop="receiptUrl" :label="`上传${props.type === 0 ? '发票' : '完税凭证'}`">
          <el-upload
            :file-list="fileList"
            class="upload-demo w-[400px]"
            :headers="{ Authorization:`Bearer ${getToken()}` }"
            :action="UploadFileUrls.normal"
            :on-error="() => { loading = false }"
            :before-upload="onBeforeUpload"
            :limit="5"
            multiple
            :on-success="(res: any) => { resolveUploaded(res) }"
            :on-remove="(file: any, uploadFiles: any) => { removeUploaded(file, uploadFiles) }"
            accept="xls,xlsx"
            drag
          >
            <svg-icon icon="material-symbols:backup-rounded" style="width: 100px ;height: 100px ;color: #909399;" />
            <div class="el-upload__text">
              将文件拖拽到这里或 <em>点击上传</em>
            </div>
            <template #tip>
              <div class="el-upload__tip text-center">
                文件格式支持：格式不限，单个文件大小不超过30MB
              </div>
            </template>
          </el-upload>
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="flex justify-end pr-6">
          <el-button @click="close">
            取消
          </el-button>
          <el-button type="primary" @click="save">
            确定
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { SvgIcon } from '@vue3/components';
import { queryInvoiceApplyFile, UploadFileUrls, uploadInvoiceApplyFile } from '@core/main';
import { ElMessage, FormInstance, FormRules, UploadProps } from 'element-plus';
import { computed, PropType, ref, watch } from 'vue';
import { EAxiosResponseCode, getToken } from '@core/api';
import { isNull } from '@core/tools';
import { IHnInvoiceApplyItem } from '@core/main/types';

const emit = defineEmits(['close', 'confirm']);

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  type: {
    type: Number as PropType<0 | 1>,
    default: 0,
  },
  row: {
    type: Object as PropType<IHnInvoiceApplyItem>,
    default: () => {},
  },
});

const loading = ref(false);
const fileList = ref<any[]>([]);
const formRef = ref<FormInstance>();
const form = ref({} as any);
const formRules = ref<FormRules<any>>({
  receiptUrl: [
    {
      required: true,
      message: `请上传${props.type === 0 ? '发票' : '完税凭证'}`,
    },
  ],
});

const title = computed(() => props.type === 0 && !props.row.offlineInvoiceStatus ? '上传发票' : props.type === 1 && !props.row.offlineTaxStatus ? '上传完税凭证' : props.type === 0 && props.row.offlineInvoiceStatus ? '重新上传发票' : '重新上传完税凭证');
const isAgainUpload = computed(() => props.row?.offlineInvoiceStatus || props.row?.offlineTaxStatus);

const onBeforeUpload: UploadProps['beforeUpload'] = async (file) => {
  loading.value = false;
  
  if (file.size / 1024 / 1024 > 30) {
    ElMessage.error('文件大小不能超过30MB');
    return false;
  }
  loading.value = true;
  return true;
};

const resolveUploaded = async (res: any) => {
  const { code, context, message } = res || {};

  loading.value = false;

  if (String(code) === EAxiosResponseCode.Succeed) {
    fileList.value.push({
      name: context?.fileName + context?.fileType,
      url: context?.fileUrl,
    });

    form.value.receiptUrl = context.fileUrl;
    formRef.value?.validateField('receiptUrl');
  } else {
    ElMessage.error(message);
  }
};

const removeUploaded = (file, files) => {
  fileList.value = fileList.value.filter((v) => v?.uid === file?.uid);
  
  if (files?.length === 0) {
    fileList.value = [];
    form.value.receiptUrl = '';
    formRef.value?.validateField('receiptUrl');
  }
};

function close() {
  formRef.value.resetFields();

  fileList.value = [];
  emit('close');
}

function save() {
  formRef.value.validate((valid) => {
    if (valid) {
      doSubmit();
    }
  });
}

async function doSubmit() {
  try {
    const parmas = {
      applyId: props.row.id,
      fileBizType: props.type,
      files: fileList.value.map((v) => v.url),
    };

    await uploadInvoiceApplyFile(parmas);

    ElMessage.success('上传成功');
    emit('confirm');
    close();
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function fetchDetail() {
  try {
    const res = await queryInvoiceApplyFile(props?.row.id, props.type);

    fileList.value = res.map((v) => ({
      name: v.slice(v.lastIndexOf('/') + 1, v?.length),
      url: v, 
    }));
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

watch(
  () => props.visible,
  (val) => {
    if (val) {
      fileList.value = [];

      if (isAgainUpload.value) fetchDetail();
    }
  },
);
</script>

<style lang="scss" scoped>
.create-agent {
  :deep(.el-dialog) {
    .el-dialog__footer {
      padding-bottom: 16px;
    }

    .el-dialog__header {
      padding-bottom: 24px;
    }
  }
}

:deep(.el-input__wrapper) {
  min-height: 40px;
}
</style>
